<template>
  <v-layout
    justify-center
    :pt-4="$vuetify.breakpoint.mdAndUp"
    wrap
  >
    <v-flex
      xs10
      sm8
    >
      <p class="required-block required-block_left">
        {{ $t('label.required_fields') }}
      </p>
      <h2 class="display-3 display-3_dark mb-3">
        {{ $t('outside_family.family_consent.title') }}
      </h2>
      <div v-if="internal">
        <p>{{ $t('outside_family.family_consent.internal_subtitle') }}</p>
        <p>{{ $t('outside_family.family_consent.internal_content') }}</p>
      </div>
      <div v-else>
        <p>{{ $t('outside_family.family_consent.subtitle') }}</p>
      </div>
      <p>{{ $t('outside_family.family_consent.alert') }}</p>
      <v-layout
        align-center
        justify-center
        wrap
        class="form"
      >
        <file-upload
          ref="upload"
          v-model="file"
          accept="image/png, image/jpeg, image/tiff, image/jpg, application/pdf"
          :drop="true"
          required
          :multiple="true"
          :maximum="maxQuantity"
          :size="maxSize"
          @input-filter="inputFilesFilter"
        >
          <p class="hidden-xs-only">
            {{ $t('buttons.upload_text') }}
          </p>
          <label
            for="file"
            class="v-btn sz_transparent"
          >{{ $t('buttons.upload_btn') }}</label>
        </file-upload>
        <p class="required-block required-block_left mb-0 mt-3">
          {{ $t('message.uploaded_files_info') }}
        </p>
      </v-layout>
      <div
        v-if="error"
        class="v-message mt-3 text-xs-center"
      >
        <div class="v-messages__message mb-0">
          {{ error }}
        </div>
      </div>
    </v-flex>
    <v-flex
      v-if="file.length > 0"
      xs10
      sm8
      mt-3
    >
      <v-layout
        v-for="item in file"
        :key="item.id"
        align-center
      >
        <v-flex
          v-if="item.type === 'application/pdf'"
          shrink
        >
          <a
            :href="item.blob"
            class="link_iconed"
            :target="target"
          >
            <baseIcon
              :width="48"
              :height="48"
            >
              <pdfIcon />
            </baseIcon>
            <span>{{ item.name }}</span>
          </a>
        </v-flex>
        <v-flex
          v-else-if="item.type === 'image/tiff'"
          shrink
        >
          <a
            :href="item.blob"
            class="link_iconed"
            :target="target"
          >
            <baseIcon
              :width="48"
              :height="48"
            >
              <tiffIcon />
            </baseIcon>
            <span>{{ item.name }}</span>
          </a>
        </v-flex>
        <v-flex
          v-else
          shrink
        >
          <a
            :href="item.blob"
            :target="target"
          >
            <img
              class="file-preview"
              :src="item.blob"
            >
          </a>
        </v-flex>
        <v-spacer />
        <v-flex
          shrink
          ml-5
        >
          <div class="text-xs-center">
            <a
              class="link_iconed"
              @click.prevent="removeFileDialog(item)"
            >
              <baseIcon
                :width="16"
                :height="16"
                class="mr-2"
              >
                <cartIcon :icon-color="$vuetify.theme.sz_violet" />
              </baseIcon>
              <span v-if="!mediumScreen">{{ $t('buttons.delete') }}</span>
            </a>
            <ConfirmDialog
              :visible="confirmBox[item.id]"
              @dialog="confirmBox[item.id] = $event"
              @response-ok="removeFile(item)"
            />
          </div>
        </v-flex>
      </v-layout>
    </v-flex>
  </v-layout>
</template>

<script>
import fileUploadConst from '@/constants/fileUploadConst';

import fileUpload from 'vue-upload-component';
import validationRules from '@/constants/validationRules';
import baseIcon from '@/icons/BaseIcon';
import cartIcon from '@/icons/CartIcon';
import pdfIcon from '@/icons/PdfIcon';
import tiffIcon from '@/icons/TiffIcon';
import ConfirmDialog from '@/components/widgets/ConfirmDialog';

import scrollToTopErrorMessage from '@/mixins/scrollToTopErrorMessage';
import fileUploadMixin from '@/mixins/fileUpload';

import { mapFields } from 'vuex-map-fields';

export default {
    name: 'AddFamilyConsent',
    components: {
        ConfirmDialog,
        fileUpload,
        baseIcon,
        cartIcon,
        pdfIcon,
        tiffIcon
    },
    mixins: [scrollToTopErrorMessage, fileUploadMixin],
    props: ['internal'],
    data () {
        return {
            validationRules,
            error: '',
            fileUploadConst,
            confirmBox: []
        };
    },
    computed: {
        ...mapFields('familyData', ['file']),
        isSafari () {
            return /^((?!chrome|android).)*safari/i.test(navigator.userAgent.toLowerCase());
        },
        target () {
            if (this.isSafari) {
                return '_self';
            } else {
                return '_blank';
            }
        },
        mediumScreen () {
            return this.$vuetify.breakpoint.mdAndDown;
        },
        smallScreen () {
            return this.$vuetify.breakpoint.smAndDown;
        },
        mobileScreen () {
            return this.$vuetify.breakpoint.xs;
        },
        location () {
            return this.$store.getters['familyData/getLocation'];
        },
        isNative () {
            return this.location && this.location.native;
        }
    },
    watch: {
        file: {
            handler (val) {
                const flag = val.length > 0;
                this.$emit('fileChanges', flag);
            },
            immediate: true
        }
    },
    methods: {
        submit () {
            if (this.file.length !== 0) {
                if (this.internal) {
                    return true;
                } else {
                    this.$router.push({ name: 'family-details-external' });
                }
                this.error = '';
            } else {
                this.error = this.$t('errors.file_required');
                this.scrollToTopErrorMessage();
                if (this.internal) {
                    return false;
                }
            }
        },
        removeFile (item) {
            this.$refs.upload.remove(item);
        },
        removeFileDialog (item) {
            this.$set(this.confirmBox, item.id, true);
        }
    }
};
</script>

<style lang="scss" scoped>
@import '../../assets/css/variables';
.file-preview {
  max-height: 200px;
  max-width: 100%;
}

@media (max-width: $md) {
  .theme--light {
    &.v-btn {
      &_remove {
        min-width: 80px;
        width: 80px;
      }
    }
  }
}
</style>
